import { useCallback, useEffect, useRef, useState } from 'react';

import { CREATE_PARAM } from 'Routes';
import { ChartType, PlayerStat } from 'types/CodegenReportTypes';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { UnitSystem } from 'types/StartupTypes';
import { useGetProfileCache } from './startupHooks';
import usePitcherReportStore from './PlayerReport/Pitcher/reportStore';
import useBatterReportStore from './PlayerReport/Batter/reportStore';
import usePitcherSingleSessionReportStore from './SessionReport/PitcherSingle/reportStore';
import usePitcherSessionReportStore from './SessionReport/Pitcher/reportStore';
import useBatterSessionReportStore from './SessionReport/Batter/reportStore';

export const useReportId = () => {
  const { reportId } = useParams();

  return { reportId, isCreate: reportId === CREATE_PARAM } as
    | { reportId: typeof CREATE_PARAM; isCreate: true }
    | { reportId: string | undefined; isCreate: false };
};

export const useChartDataDisplay = <ChartData>(
  chartData: ChartData | undefined,
  simulateChartData: (isLoading: boolean) => ChartData,
  isLoading: boolean
) => {
  const [chartDataDisplay, setChartDataDisplay] = useState<ChartData>(chartData ?? simulateChartData(false));
  const intervalIdRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined;
      }
    };
  }, []);

  // This useEffect is a bit tricky to get right – changing it can easily cause an infinite loop, proceed with caution.
  useEffect(() => {
    if (isLoading) {
      // If an interval is already running, clear it before setting a new one
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      setChartDataDisplay(simulateChartData(true));
      const interval = setInterval(() => {
        setChartDataDisplay(simulateChartData(true));
      }, 1500);
      intervalIdRef.current = interval; // Store the new interval ID in the ref
    } else {
      // Clear the interval if it's running
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined; // Clear the ref
      }
      setChartDataDisplay(chartData ?? simulateChartData(false));
    }
  }, [isLoading, chartData, simulateChartData]);

  return chartDataDisplay;
};

export const useChartTitle = () => {
  const { t } = useTranslation();

  const { selectedConfgValues: pitcherReportSelectedConfigValues } = usePitcherReportStore();
  const { selectedConfgValues: batterReportSelectedConfigValues } = useBatterReportStore();
  const { selectedConfgValues: pitcherSingleSessionReportSelectedConfigValues } = usePitcherSingleSessionReportStore();
  const { selectedConfgValues: pitcherSessionReportSelectedConfigValues } = usePitcherSessionReportStore();
  const { selectedConfgValues: batterSessionReportSelectedConfigValues } = useBatterSessionReportStore();

  const unitSystem = useGetUnitSystem();

  const getChartTitle = useCallback(
    (chartId: ChartType, extraChartData: any) => {
      switch (chartId) {
        case 'PitcherHeatmapChart':
        case 'BatterHeatmapChart':
        case 'PitcherSingleSessionHeatmapChart':
        case 'PitcherSessionHeatmapChart':
        case 'BatterSessionHeatmapChart':
          return (extraChartData.viewDataAs === 'PitchLocation' ? t('PitchLocation') : t(extraChartData.playerStat)) + ' ' + t('heatmap');
        case 'PitcherScatterChart_1':
        case 'PitcherScatterChart_2':
        case 'PitcherScatterChart_3':
        case 'PitcherSessionScatterChart_1':
        case 'PitcherSessionScatterChart_2':
        case 'PitcherSessionScatterChart_3':
        case 'PitcherSingleSessionScatterChart_1':
        case 'PitcherSingleSessionScatterChart_2':
        case 'PitcherSingleSessionScatterChart_3':
          return t(extraChartData.xAxis) + ' vs ' + t(extraChartData.yAxis);
        case 'PitchTypeStatsChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherReportSelectedConfigValues.pitchGrouping);
        case 'BatterUsedPitchTypesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(batterReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSingleSessionStatsByPitchChart':
        case 'PitcherSingleSessionPitchTypeRangesChart':
        case 'PitcherSessionStatsByPitchTypeChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherSingleSessionReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSessionPitchTypeRangesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherSessionReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSingleSessionPitchByPitchLineChart_1':
        case 'PitcherSingleSessionPitchByPitchLineChart_2':
        case 'PitcherSingleSessionPitchByPitchLineChart_3':
        case 'PitcherSingleSessionPitchByPitchLineChart_4':
          return t(extraChartData.yAxis) + ' ' + t('byPitch');
        case 'PitcherSessionTimelineChart_1':
        case 'PitcherSessionTimelineChart_2':
        case 'PitcherSessionTimelineChart_3':
        case 'PitcherSessionTimelineChart_4':
          return t(extraChartData.yAxis) + ' ' + t('byGame');
        case 'BatterSprayChart':
        case 'BatterSessionSprayChart':
          return extraChartData.viewChartAs === 'SprayChart' ? t('ChartTitle.BatterSprayChart') : t('ChartTitle.BatterSprayZoneChart');
        case 'BatterSessionStatsByPitchTypeChart':
          return t('ChartTitle.' + chartId) + ' ' + t(batterSessionReportSelectedConfigValues.pitchGrouping);
        case 'BatterSingleSessionBattingStatChart_1':
        case 'BatterSingleSessionBattingStatChart_2':
        case 'BatterSingleSessionBattingStatChart_3':
        case 'BatterSingleSessionBattingStatChart_4':
        case 'BatterSingleSessionBattingStatChart_5':
        case 'BatterSingleSessionBattingStatChart_6':
          return (extraChartData.playerStat as PlayerStat) === 'BattingAverage' ? t('HitsPerAtBats') : t(extraChartData.playerStat);
        case 'BatterSessionTimelineChart_1':
        case 'BatterSessionTimelineChart_2':
        case 'BatterSessionTimelineChart_3':
        case 'BatterSessionTimelineChart_4':
          return t(extraChartData.playerStat) + ' ' + t('OverTime');
        default:
          return t('ChartTitle.' + chartId);
      }
    },
    [
      pitcherReportSelectedConfigValues,
      batterReportSelectedConfigValues,
      pitcherSingleSessionReportSelectedConfigValues,
      pitcherSessionReportSelectedConfigValues,
      batterSessionReportSelectedConfigValues,
    ]
  );

  const getChartSubTitle = useCallback((chartId: ChartType, extraChartData: any) => {
    switch (chartId) {
      case 'PitcherHeatmapChart':
      case 'BatterHeatmapChart':
      case 'PitcherSingleSessionHeatmapChart':
      case 'PitcherSessionHeatmapChart':
      case 'BatterSessionHeatmapChart':
        if (extraChartData.viewDataAs === 'PlayerStat' && extraChartData.playerStat === 'ExitSpeed') {
          return unitSystem === 'Metric' ? 'kph' : 'mph';
        }
      case 'PitcherSingleSessionPitchTypeRangesChart':
      case 'PitcherSessionPitchTypeRangesChart':
        return t(extraChartData.selectedMeasurementOption);
      case 'BatterSingleSessionPitchLocationChart':
      case 'BatterSingleSessionContactPointChart':
        return t('pitchOfGame');
      case 'UmpireStrikeZoneChart':
        return t('BatterView');
      default:
        return undefined;
    }
  }, []);

  const getChartStats = useCallback((chartId: ChartType, extraChartData: any) => {
    switch (chartId) {
      case 'PitchingStatsChart':
      case 'PitchTypeStatsChart':
      case 'PitcherBallTrackingStatsChart':
      case 'BatterUsedPitchTypesChart':
      case 'BatterBallStrikeSummaryChart':
      case 'BattingStatsChart':
      case 'BatterSingleSessionGameStatsChart':
      case 'BatterSingleSessionPitchByPitchStatsChart':
      case 'BatterSessionStatsByPitchTypeChart':
      case 'PitcherSingleSessionStatsByPitchChart':
      case 'PitcherSingleSessionPitchByPitchStatsChart':
      case 'PitcherSingleSessionGameStatsChart':
      case 'PitcherSessionStatsByPitchTypeChart':
        return extraChartData.visibleStats?.length ? `${extraChartData.visibleStats.length} ${t('stats')}` : '';

      case 'PitcherScatterChart_1':
      case 'PitcherScatterChart_2':
      case 'PitcherScatterChart_3':
      case 'PitcherSessionScatterChart_1':
      case 'PitcherSessionScatterChart_2':
      case 'PitcherSessionScatterChart_3':
      case 'PitcherSingleSessionScatterChart_1':
      case 'PitcherSingleSessionScatterChart_2':
      case 'PitcherSingleSessionScatterChart_3':
        return extraChartData.xAxis && extraChartData.yAxis ? `${t(extraChartData.xAxis)}, ${t(extraChartData.yAxis)}` : '';

      case 'PitcherPercentileRankingsChart':
      case 'BatterPercentileRankingsChart':
        return extraChartData.viewDataAs && extraChartData.visibleStats?.length
          ? `${t(extraChartData.viewDataAs)}, ${extraChartData.visibleStats.length} ${t('stats')}`
          : '';

      case 'BatterSingleSessionPitchLocationChart':
        return extraChartData.pitchLocationBy ? t(extraChartData.pitchLocationBy) : '';

      case 'BatterSingleSessionContactPointChart':
        return extraChartData.contactPointBy ? t(extraChartData.contactPointBy) : '';

      case 'PitcherSingleSessionPitchTypeRangesChart':
      case 'PitcherSessionPitchTypeRangesChart':
        return extraChartData.selectedMeasurementOption ? t(extraChartData.selectedMeasurementOption) : '';

      default:
        return '';
    }
  }, []);

  const getHideChartTitleInGrid = useCallback((chartId: ChartType) => {
    switch (chartId) {
      case 'BatterApproachPerCountChart':
        return true;
      default:
        return false;
    }
  }, []);

  return { getChartTitle, getChartSubTitle, getChartStats, getHideChartTitleInGrid };
};

export const useGetUnitSystem = (): UnitSystem => {
  const profile = useGetProfileCache();

  if (profile?.preferredUnitSystem === 'Metric') {
    return 'Metric';
  }

  return 'Imperial';
};
